import { resolveRoute } from '@/routes/routes'
import {
  checkoutLogin,
  cleanLoginStorage,
  getSearch,
  getUrlPath,
  getUrlQuery,
  goToLogin,
  jumpWhiteList,
  loginWhiteList,
  logOut,
  redirectByUrl,
  storageTidKey,
  getTokenTenantId,
  ACCOUNT_MODE,
  isInStudent,
} from '@yuanfudao/metis-unified-login'
import { AccountInfo, ChooseSchool, CHOOSE_ERROR_CODE, CreatePersonalSchool, NoPermission, PerfectData, PersonalData } from '@yuanfudao/metis-unified-login/es/components'
import { UNIVERSAL_CONTEXT, useAccount } from '@yuanfudao/metis-standard-data'
import { useGlobalState } from '@/store'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { BridgeSwitchSchool } from '@/electron'
import icBack from '@/assets/login2/reset-back.png'
import ForgotPassword from '@/pages/forgot-password'
import { LOGIN_TYPE, loginTypeStorageKey } from '@/pages/login'
import lessModule from './index.module.less'
import { useMsgFail, useMsgSuccess } from '@yuanfudao/metis-unified-login/es/utils/antd-message'

export enum EMBED_TYPE {
  NO_PERMISSION = '403',
  CHOOSE = 'choose',
  INFO = 'info',
  RESET_PASSWORD = 'reset-password',
  PERSONAL_DATA = 'personal-data',
  ACCOUNT_INFO = 'account-info',
  CREATE_PERSONAL_SCHOOL = 'create-personal-school',
}

export default function Embed() {
  const { type } = useParams<{type: EMBED_TYPE}>()
  const { globalOk, updateGlobalState } = useGlobalState()
  const history = useHistory()
  const [curTenantId, setCurTenantId] = useState<number>()
  const accountData = useAccount()
  const { userTenants, teacherTenantName, updateAccount } = accountData

  const MsgSuccess = useMsgSuccess()
  const MsgFail = useMsgFail()

  const currentAccount = useMemo(() => {
    if (accountData.accountId) {
      return {
        accountId: accountData.accountId as number,
        avatar: accountData.accountAvatar as string,
        email: '',
        nickname: accountData.accountName as string,
        phone: accountData.accountPhone as string,
        wxBound: accountData.accountWxBound as boolean,
        wxNickName: accountData.accountWxNickname as string,
        hasPassword: accountData.accountHasPassword as boolean,
      }
    }
    return null
  }, [accountData.accountAvatar, accountData.accountHasPassword, accountData.accountId, accountData.accountName, accountData.accountPhone, accountData.accountWxBound, accountData.accountWxNickname])

  const onChooseError = useCallback(({ code, message }) => {
    if (code === CHOOSE_ERROR_CODE.UNAUTHORIZED) {
      history.push(`${resolveRoute({ name: 'login' })}${getSearch()}`)
      return
    }
    MsgFail(message)
  }, [MsgFail, history])

  const onPerfectSuccess = useCallback(async () => {
    try {
      localStorage.setItem(storageTidKey, String(getTokenTenantId()) || '')
      redirectByUrl()
      // 新登录流程，完善个人信息之后，不需要判断是否选学校，选学校改为由业务自己控制
    } catch (e) {
      console.warn(e)
    }
  }, [])

  const onAccountInfoSuccess = useCallback(async () => {
    try {
      localStorage.setItem(storageTidKey, String(getTokenTenantId()) || '')
      redirectByUrl()
    } catch (e) {
      console.warn(e)
    }
  }, [])

  const onClickBack = () => {
    if (type === EMBED_TYPE.NO_PERMISSION) {
      const redirect = getUrlQuery().redirectUrl as string
      if (redirect) {
        window.location.href = redirect.split('#')[0]
      } else {
        history.go(-1)
      }
      return
    }
    history.go(-1)
  }

  const showingComponent = useMemo(() => {
    switch (type) {
      case EMBED_TYPE.CHOOSE: return (
        <ChooseSchool
          tenants={userTenants}
          curTenantId={curTenantId}
          onSuccess={() => {
            BridgeSwitchSchool()
            updateGlobalState()
            redirectByUrl()
          }}
          onError={onChooseError}
        />
      )
      case EMBED_TYPE.INFO: return (
        <PerfectData
          style={{ paddingTop: '48px' }}
          currentAccount={currentAccount}
          tenantName={teacherTenantName}
          onSuccess={onPerfectSuccess}
        />
      )
      case EMBED_TYPE.ACCOUNT_INFO: return (
        <AccountInfo
          mode={isInStudent() ? ACCOUNT_MODE.STUDENT : ACCOUNT_MODE.ACCOUNT}
          currentAccount={currentAccount}
          tenantName={!isInStudent() ? teacherTenantName : undefined}
          onSuccess={onAccountInfoSuccess}
          onCancel={() => { history.go(-1) }}
        />
      )
      case EMBED_TYPE.CREATE_PERSONAL_SCHOOL: return (
        currentAccount && (
          <CreatePersonalSchool
            accountInfo={currentAccount}
            onSuccess={onAccountInfoSuccess}
            onError={MsgFail}
            onCancel={() => { history.go(-1) }}
          />
        )
      )
      case EMBED_TYPE.NO_PERMISSION: return (
        <NoPermission />
      )
      case EMBED_TYPE.RESET_PASSWORD: return (
        <ForgotPassword
          onCancel={() => {
            localStorage.setItem(loginTypeStorageKey, LOGIN_TYPE.PASSWORD)
            history.replace(`${resolveRoute({ name: 'login', params: { param: '' } })}${getSearch()}`)
          }}
          onSuccess={() => {
            MsgSuccess('密码重置成功，请重新登录')
            logOut(false)
            cleanLoginStorage()
            window.setTimeout(() => {
              history.push(`${resolveRoute({ name: 'login', params: { param: '' } })}${getSearch()}`)
            }, 3000)
          }}
        />
      )
      case EMBED_TYPE.PERSONAL_DATA: return currentAccount ? (
        <PersonalData
          currentAccount={currentAccount}
          tenantName={teacherTenantName}
          onUnBindSuccess={() => {
            updateAccount()
            updateAccount(UNIVERSAL_CONTEXT.ACCOUNT)
          }}
          onPerfectDataSuccess={() => {
            MsgSuccess('保存成功')
            updateGlobalState()
          }}
          onChangePhoneSuccess={() => {
            MsgSuccess('修改手机号成功，请重新登录')
            logOut(false)
            cleanLoginStorage()
            window.setTimeout(() => {
              goToLogin()
            }, 3000)
          }}
          onResetPasswordSuccess={() => {
            MsgSuccess('密码重置成功，请重新登录')
            logOut(false)
            cleanLoginStorage()
            window.setTimeout(() => {
              goToLogin()
            }, 3000)
          }}
          onWxBindSuccess={() => {
            MsgSuccess('绑定微信成功')
            // updateGlobalState()
            setTimeout(() => {
              window.location.reload()
            }, 3000)
          }}
        />
      ) : null
      default: return null
    }
  }, [type, userTenants, curTenantId, onChooseError, currentAccount, teacherTenantName, onPerfectSuccess, onAccountInfoSuccess, MsgFail, updateGlobalState, history, MsgSuccess, updateAccount])

  useEffect(() => {
    const init = async () => {
      const curPath = getUrlPath()
      const isWhiteJumpPaht = jumpWhiteList.some((path) => curPath.startsWith(path))
      if (loginWhiteList.some((path) => curPath.startsWith(path))) {
        return
      }
      const login = await checkoutLogin()
      if (globalOk) {
        if (login) {
          if (userTenants.length !== 0) {
            const curTid = getTokenTenantId()
            if (curTid) {
              for (let i = 0; i < userTenants.length; i++) {
                if (userTenants[i].tenantId === curTid) {
                  setCurTenantId(curTid)
                  break
                }
              }
            }
          }
          if (getUrlQuery().switchSchool !== '1') {
            if (!isWhiteJumpPaht) {
              redirectByUrl()
            }
          }
        }
      }
    }
    init()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalOk])

  if (type === EMBED_TYPE.RESET_PASSWORD) return <>{showingComponent}</>

  return (
    <div className={lessModule.wrap}>
      <div className={lessModule.backWrap}>
        <img
          className={lessModule.back}
          src={icBack}
          alt=""
          onClick={onClickBack}
        />
      </div>
      <div
        className={lessModule.componentWrap}
      >
        { showingComponent }
      </div>
    </div>
  )
}
